import React, {forwardRef, useCallback, useImperativeHandle, useState} from "react";
import FileUploader from "../../../../components/files/FileUploader";
import {saveCompany} from "../../../../api/company/CompanyService";
import {validationChecker} from "../../../../components/commonmethod/CommonMethod";
import {SelectComboBoxForCreate} from "../../../../components/select/SelectComboBoxForCreate";
import Th_title from "../../../../components/Th_title";
import Section from "../../../../components/section/Section";
import {inputOnChange} from "../../../../components/commonmethod/simmsInputMethod";
import SimmsInput from "../../../../components/input/SimmsInput";
import SimmsTextArea from "../../../../components/input/SimmsTextArea";
import {handleActiveError, handleActiveToast} from "../../../../components/sonner/ToastBasic";
import {apiLoading} from "../../../../redux/slice/apiSlice";
import {useDispatch} from "react-redux";
import {useCacheRefresh} from "../../../../hooks/useCacheRefresh";
import {CacheType} from "../../../../constant/CodeSpecNum";
import ImageUploader from "../../../../components/files/ImageUploader";

export const companyOperation = [
    {id: "일반업체", name: "일반업체"},
    {id: "운영업체", name: "운영업체"},
];

export const companyClassification = [
    {id: "없음", name: "없음"},
    {id: "신규", name: "신규"},
    {id: "통합", name: "통합"},
];

const CompanyCreateForm = forwardRef(function CompanyCreateForm(
    {embedded = false, hideActions = false, submitLabel = "등록", onSuccess, onCancel},
    ref,
) {
    const [company, setCompany] = useState({operation: "일반업체", classification: "없음"});
    const [docs, setDocs] = useState([]);
    const [images, setImages] = useState([]);
    const refreshCache = useCacheRefresh();
    const dispatch = useDispatch();

    const submit = useCallback(async () => {
        if (!validationChecker(company)) {
            return;
        }

        dispatch(apiLoading(true));
        const imageFile = [];
        const docFile = [];
        images.forEach((each) => imageFile.push(each.object));
        docs.forEach((each) => docFile.push(each.object));

        try {
            const response = await saveCompany(company, imageFile, docFile);
            if (response.status === 200) {
                refreshCache(CacheType.company);
                handleActiveToast({message: "등록되었습니다."});
                onSuccess?.();
            } else if (response.status === 201 || response.status === 202) {
                handleActiveError({message: response.data.title, description: response.data.reason});
            }
        } finally {
            dispatch(apiLoading(false));
        }
    }, [company, dispatch, docs, images, onSuccess, refreshCache]);

    useImperativeHandle(ref, () => ({submit}), [submit]);

    return (
        <div className={`company-manage-create ${embedded ? "company-manage-create--embedded" : ""}`}>
            <div className="company-manage-create__body code-manage-scroll-area">
                <Section title="기본정보">
                    <table className="w-full create-table">
                        <tbody>
                            <tr>
                                <Th_title title="회사명칭" colspan={1} className="fieldset-td" required auth>
                                    <SimmsInput onChange={(v) => inputOnChange(setCompany, "name", v, true)} data={company.name}/>
                                </Th_title>
                                <Th_title title="관리번호" colspan={1} className="fieldset-td" auth>
                                    <SimmsInput onChange={(v) => inputOnChange(setCompany, "number", v, false)} data={company.number}/>
                                </Th_title>
                            </tr>
                            <tr>
                                <Th_title title="사무실번호" colspan={1} className="fieldset-td" auth>
                                    <SimmsInput onChange={(v) => inputOnChange(setCompany, "officePhone", v, false)} data={company.officePhone}/>
                                </Th_title>
                                <Th_title title="대표연락망" colspan={1} className="fieldset-td" auth>
                                    <SimmsInput onChange={(v) => inputOnChange(setCompany, "employeeContact", v, false)} data={company.employeeContact}/>
                                </Th_title>
                            </tr>
                            <tr>
                                <Th_title title="팩스번호" colspan={1} className="fieldset-td" auth>
                                    <SimmsInput onChange={(v) => inputOnChange(setCompany, "fax", v, false)} data={company.fax}/>
                                </Th_title>
                                <Th_title title="비상연락망" colspan={1} className="fieldset-td" auth>
                                    <SimmsInput onChange={(v) => inputOnChange(setCompany, "emergencyPhone", v, false)} data={company.emergencyPhone}/>
                                </Th_title>
                            </tr>
                            <tr>
                                <Th_title title="홈페이지" colspan={1} className="fieldset-td" auth>
                                    <SimmsInput onChange={(v) => inputOnChange(setCompany, "uri", v, false)} data={company.uri}/>
                                </Th_title>
                                <Th_title title="이메일" colspan={1} className="fieldset-td" auth>
                                    <SimmsInput onChange={(v) => inputOnChange(setCompany, "email", v, false)} data={company.email}/>
                                </Th_title>
                            </tr>
                            <tr>
                                <Th_title title="주소" colspan={3} className="fieldset-td" auth>
                                    <SimmsInput onChange={(v) => inputOnChange(setCompany, "address", v, false)} data={company.address}/>
                                </Th_title>
                            </tr>
                            <tr>
                                <Th_title title="비고" colspan={3} className="fieldset-td" auth>
                                    <SimmsTextArea onChange={(v) => inputOnChange(setCompany, "etc", v, false)} data={company.etc} style={{minHeight: "80px"}}/>
                                </Th_title>
                            </tr>
                            <tr>
                                <Th_title title="운영여부" colspan={1} className="fieldset-td" auth>
                                    <SelectComboBoxForCreate target="operation" options={companyOperation} data={{data: company, fnc: setCompany}} defaultValue={company.operation}/>
                                </Th_title>
                                <Th_title title="업체구분" colspan={1} className="fieldset-td" auth>
                                    <SelectComboBoxForCreate target="classification" options={companyClassification} data={{data: company, fnc: setCompany}} defaultValue={company.classification}/>
                                </Th_title>
                            </tr>
                        </tbody>
                    </table>
                </Section>

                <Section title="이미지관리">
                    <article className="w-full">
                        <ImageUploader filed={{files: images, setFiles: setImages}}/>
                    </article>
                </Section>

                <Section title="파일업로드">
                    <FileUploader filed={{files: docs, setFiles: setDocs}}/>
                </Section>
            </div>

            {!hideActions && (
                <div className="company-manage-create__actions code-manage-scope">
                    <button type="button" className="code-manage-neutral-btn" onClick={onCancel}>
                        취소
                    </button>
                    <button type="button" className="save-btn" onClick={submit}>
                        {submitLabel}
                    </button>
                </div>
            )}
        </div>
    );
});

export default CompanyCreateForm;
